import type {
  ApplicationFieldConfig,
  ApplicationFieldType,
  ApplicationStructure,
} from '../types/audition.types';
import {
  flattenApplicationGroups,
  isValidApplicationStructure,
  toApplicationGroups,
} from './application-structure';
import { ensureEnforcedFields } from './competition-enforced-fields';

/**
 * Registry powering the dynamic form-field columns on the organizer
 * Participants table (BAL-589). Resolves the audition's application
 * structure the same way the apply flow does (valid-structure guard +
 * competition enforced fields), then keeps only answer-scalar fields —
 * media / experience / institution answers stay in the detail panel
 * (BAL-562 owns that surface).
 */

/** Field types that produce a scalar (or scalar-list) answer worth a column. */
const ANSWER_COLUMN_TYPES = new Set<ApplicationFieldType>([
  'text',
  'textarea',
  'number',
  'email',
  'phone',
  'date',
  'select',
  'multiselect',
  'checkbox',
  'checkboxGroup',
  'radio',
  'url',
  'age_category',
]);

/** Filter widget the table column should render, derived from the field type. */
export type ParticipantColumnFilter = 'text' | 'select' | 'multi-select';

export interface ParticipantFieldColumn {
  key: string;
  label: string;
  type: ApplicationFieldType;
  filter: ParticipantColumnFilter;
  /** Sort numerically (number) or lexicographically on ISO dates (date). */
  sort: 'text' | 'number' | 'date';
  /** Declared options (value -> label) for select-family fields, if any. */
  options: Array<{ value: string; label: string }>;
}

export type ParticipantAnswerValue =
  | string
  | number
  | boolean
  | string[]
  | null;

function filterVariantFor(type: ApplicationFieldType): ParticipantColumnFilter {
  switch (type) {
    case 'select':
    case 'radio':
    case 'age_category':
    case 'checkbox':
      return 'select';
    case 'multiselect':
    case 'checkboxGroup':
      return 'multi-select';
    default:
      return 'text';
  }
}

function sortModeFor(type: ApplicationFieldType): 'text' | 'number' | 'date' {
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return 'text';
}

function optionLabel(
  option: NonNullable<ApplicationFieldConfig['options']>[number],
): { value: string; label: string } {
  const value = String(option.value ?? '');
  return { value, label: option.label ? String(option.label) : value };
}

/**
 * Resolve the audition's raw `application_structure` into the ordered list of
 * answer-scalar field columns. Mirrors the apply-flow resolution: foreign /
 * null blobs fall back to an empty structure, competitions get the enforced
 * booklet + positional fields injected so their columns always exist.
 */
export function resolveParticipantFieldColumns(
  structureRaw: unknown,
  isCompetition: boolean,
): ParticipantFieldColumn[] {
  const base: ApplicationStructure = isValidApplicationStructure(structureRaw)
    ? structureRaw
    : { version: 1, fields: [] };

  const structure = isCompetition ? ensureEnforcedFields(base) : base;
  const fields = flattenApplicationGroups(toApplicationGroups(structure));

  const seen = new Set<string>();
  const columns: ParticipantFieldColumn[] = [];

  for (const field of fields) {
    if (!ANSWER_COLUMN_TYPES.has(field.type)) continue;
    if (!field.key || seen.has(field.key)) continue;
    seen.add(field.key);

    columns.push({
      key: field.key,
      label: field.label || field.key,
      type: field.type,
      filter: filterVariantFor(field.type),
      sort: sortModeFor(field.type),
      options: (field.options ?? []).map(optionLabel),
    });
  }

  return columns;
}

/**
 * Project one application's `fields` JSONB answer onto a column as a display
 * scalar. Select-family values map through declared option labels; arrays stay
 * arrays of labels; checkbox coerces to boolean; number/date pass through
 * (dates stay ISO strings — lexicographic sort is chronological). Anything
 * non-scalar (objects, nested blobs) renders as null rather than leaking JSON.
 */
export function projectAnswerValue(
  fields: unknown,
  column: ParticipantFieldColumn,
): ParticipantAnswerValue {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return null;
  }
  const raw = (fields as Record<string, unknown>)[column.key];
  if (raw === null || raw === undefined || raw === '') return null;

  const labelFor = (value: unknown): string => {
    const v = String(value);
    return column.options.find((o) => o.value === v)?.label ?? v;
  };

  if (column.type === 'checkbox') {
    if (typeof raw === 'string') {
      return !['false', 'no', '0', 'off'].includes(raw.toLowerCase());
    }
    return Boolean(raw);
  }

  if (Array.isArray(raw)) {
    const labels = raw
      .filter((v) => typeof v !== 'object' || v === null)
      .map(labelFor)
      .filter((v) => v.length > 0);
    return labels.length > 0 ? labels : null;
  }

  if (typeof raw === 'number') return raw;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'object') return null;

  if (column.type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : String(raw);
  }

  return labelFor(raw);
}

/**
 * Build the per-candidate answers record for the registry's columns.
 * Only registry keys are projected — the raw JSONB (which can carry media
 * arrays and foreign blobs) never reaches the client payload (BAL-574 lesson).
 */
export function projectAnswers(
  fields: unknown,
  columns: ParticipantFieldColumn[],
): Record<string, ParticipantAnswerValue> {
  const out: Record<string, ParticipantAnswerValue> = {};
  for (const column of columns) {
    out[column.key] = projectAnswerValue(fields, column);
  }
  return out;
}
